import React, { useState, useRef } from 'react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { exportToCSV, exportToPNG, ExportButton } from './exportUtils';
import { CHART_TYPE_COLORS, CHART_COLOR_SEQUENCE, generateShades } from '../../utils/chartColors';

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (active && payload && payload.length) {
    // Filter out hidden series from tooltip
    const visiblePayload = payload.filter(entry => entry.value !== undefined && entry.value !== null);
    if (visiblePayload.length === 0) return null;

    return (
      <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-gray-200">
        <p className="text-sm font-medium text-gray-900 mb-1">{label}</p>
        {visiblePayload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {formatter ? formatter(entry.value) : entry.value}
            {entry.payload?.unit || ''}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Custom Legend component that always shows all series
const CustomLegend = ({ yKeys, colors, hiddenSeries, onToggle }) => {
  return (
    <div className="flex flex-wrap justify-center gap-4 mt-2">
      {yKeys.map((key, index) => {
        const isHidden = hiddenSeries.has(key);
        const color = colors[index % colors.length];
        return (
          <button
            key={key}
            onClick={() => onToggle(key)}
            className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
            style={{ cursor: 'pointer' }}
          >
            <span
              className="w-3 h-3 rounded-sm"
              style={{
                backgroundColor: isHidden ? '#d1d5db' : color,
                opacity: isHidden ? 0.5 : 1,
              }}
            />
            <span
              className="text-sm"
              style={{
                color: isHidden ? '#9ca3af' : '#374151',
                textDecoration: isHidden ? 'line-through' : 'none',
              }}
            >
              {key}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const InteractiveChart = ({
  type = 'bar',
  data = [],
  title,
  description,
  xKey = 'name',
  yKeys = ['value'],
  colors,
  height = 300,
  showLegend = true,
  showGrid = true,
  formatter,
  stacked = false,
  layout = 'horizontal',
  showExport = true,
}) => {
  const typeColors = CHART_TYPE_COLORS[type];
  const baseColors = colors || (Array.isArray(typeColors) ? typeColors : null) || CHART_COLOR_SEQUENCE;
  // Auto-generate shades when data/series exceed the base palette to avoid repeating colors
  const neededColors = type === 'pie' ? data.length : yKeys.length;
  const chartColors = generateShades(baseColors, neededColors);
  const [activeIndex, setActiveIndex] = useState(null);
  const [hiddenSeries, setHiddenSeries] = useState(new Set());
  const chartRef = useRef(null);

  const handleExportCSV = () => {
    const filename = title ? title.replace(/[^a-z0-9]/gi, '_').toLowerCase() : 'chart_data';
    exportToCSV(data, filename);
  };

  const handleExportPNG = () => {
    const filename = title ? title.replace(/[^a-z0-9]/gi, '_').toLowerCase() : 'chart';
    exportToPNG(chartRef.current, filename);
  };

  const toggleSeries = (dataKey) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(dataKey)) {
        next.delete(dataKey);
      } else {
        next.add(dataKey);
      }
      return next;
    });
  };

  const formatValue = (value) => {
    if (formatter) return formatter(value);
    if (typeof value === 'number') {
      return value.toLocaleString(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      });
    }
    return value;
  };

  const renderChart = () => {
    const commonProps = {
      data,
      margin: { top: 10, right: 30, left: 0, bottom: 0 },
    };

    switch (type) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
            <XAxis
              dataKey={xKey}
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
            />
            <YAxis
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
              tickFormatter={formatValue}
            />
            <Tooltip content={<CustomTooltip formatter={formatValue} />} />
            {yKeys.map((key, index) => (
              <Line
                key={key}
                type="monotone"
                dataKey={key}
                stroke={chartColors[index % chartColors.length]}
                strokeWidth={2}
                dot={{ r: 4, fill: chartColors[index % chartColors.length] }}
                activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
                hide={hiddenSeries.has(key)}
              />
            ))}
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
            <XAxis
              dataKey={xKey}
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
            />
            <YAxis
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
              tickFormatter={formatValue}
            />
            <Tooltip content={<CustomTooltip formatter={formatValue} />} />
            {yKeys.map((key, index) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stackId={stacked ? '1' : undefined}
                stroke={chartColors[index % chartColors.length]}
                fill={chartColors[index % chartColors.length]}
                fillOpacity={0.3}
                hide={hiddenSeries.has(key)}
              />
            ))}
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              dataKey={yKeys[0] || 'value'}
              nameKey={xKey}
              onMouseEnter={(_, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={chartColors[index % chartColors.length]}
                  opacity={activeIndex === null || activeIndex === index ? 1 : 0.5}
                  style={{ cursor: 'pointer', transition: 'opacity 0.2s' }}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip formatter={formatValue} />} />
          </PieChart>
        );

      case 'bar':
      default:
        return (
          <BarChart {...commonProps} layout={layout}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />}
            <XAxis
              dataKey={layout === 'horizontal' ? xKey : undefined}
              type={layout === 'horizontal' ? 'category' : 'number'}
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
              tickFormatter={layout === 'vertical' ? formatValue : undefined}
            />
            <YAxis
              dataKey={layout === 'vertical' ? xKey : undefined}
              type={layout === 'vertical' ? 'category' : 'number'}
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickLine={{ stroke: '#d1d5db' }}
              axisLine={{ stroke: '#d1d5db' }}
              tickFormatter={layout === 'horizontal' ? formatValue : undefined}
              width={layout === 'vertical' ? 120 : 60}
            />
            <Tooltip content={<CustomTooltip formatter={formatValue} />} />
            {yKeys.map((key, index) => (
              <Bar
                key={key}
                dataKey={key}
                stackId={stacked ? 'stack' : undefined}
                fill={chartColors[index % chartColors.length]}
                hide={hiddenSeries.has(key)}
                radius={stacked ? 0 : [4, 4, 0, 0]}
                onMouseEnter={(_, i) => setActiveIndex(i)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {data.map((entry, i) => (
                  <Cell
                    key={`cell-${i}`}
                    fill={chartColors[index % chartColors.length]}
                    opacity={activeIndex === null || activeIndex === i ? 1 : 0.7}
                    style={{ transition: 'opacity 0.2s' }}
                  />
                ))}
              </Bar>
            ))}
          </BarChart>
        );
    }
  };

  return (
    <div className="my-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100">
      <div className="flex justify-between items-start mb-2">
        <div>
          {title && (
            <h4 className="text-base font-semibold text-gray-900 mb-1">{title}</h4>
          )}
          {description && (
            <p className="text-sm text-gray-500">{description}</p>
          )}
        </div>
        {showExport && data.length > 0 && (
          <ExportButton
            onExportCSV={handleExportCSV}
            onExportPNG={handleExportPNG}
            showPNG={true}
          />
        )}
      </div>
      <div ref={chartRef}>
        <ResponsiveContainer width="100%" height={height}>
          {renderChart()}
        </ResponsiveContainer>
      </div>
      {showLegend && type !== 'pie' && yKeys.length > 1 && (
        <CustomLegend
          yKeys={yKeys}
          colors={chartColors}
          hiddenSeries={hiddenSeries}
          onToggle={toggleSeries}
        />
      )}
      {showLegend && type === 'pie' && (
        <div className="flex flex-wrap justify-center gap-3 mt-3">
          {data.map((entry, index) => (
            <div key={index} className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: chartColors[index % chartColors.length] }}
              />
              <span className="text-sm text-gray-700">{entry[xKey]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default InteractiveChart;
